import { useNavigate } from 'react-router-dom';
import { useGuest } from '../context/GuestContext';
import { useAuth } from '../context/AuthContext';
import { X, Sparkles, Shield, CloudOff } from 'lucide-react';
import { Button } from './ui/button';

// Soft prompt banner - shows after 5-7 actions
export function SoftPromptBanner() {
  const { showSoftPrompt, dismissSoftPrompt, searchesUsed, guestSearchLimit } = useGuest();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!showSoftPrompt || user) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-primary/95 to-green-600/95 backdrop-blur-sm shadow-lg animate-slide-up">
      <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="text-white">
            <p className="font-semibold text-sm">Enjoying FlipValue?</p>
            <p className="text-xs text-white/80">
              Create a free account to save your data ({searchesUsed}/{guestSearchLimit} used)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white text-primary hover:bg-white/90 font-semibold"
            onClick={() => {
              dismissSoftPrompt();
              navigate('/auth');
            }}
          >
            Sign Up
          </Button>
          <button
            onClick={dismissSoftPrompt}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Full-screen limit overlay - shows when 10 actions reached
export function LimitOverlay() {
  const { showLimitOverlay, dismissLimitOverlay } = useGuest();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!showLimitOverlay || user) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-scale-up">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          
          <h2 className="text-xl font-bold mb-2">Guest Limit Reached</h2>
          <p className="text-muted-foreground mb-6">
            You've used all 5 free searches. Create a free account to get unlimited searches and save your data across devices.
          </p>

          <div className="space-y-3">
            <Button
              className="w-full h-12 text-base font-semibold"
              onClick={() => {
                dismissLimitOverlay();
                navigate('/auth');
              }}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Create Free Account
            </Button>
            
            <button
              onClick={dismissLimitOverlay}
              className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Continue browsing
            </button>
          </div>

          <p className="text-xs text-muted-foreground mt-4">
            Sign up for free to unlock unlimited access!
          </p>
        </div>
      </div>
    </div>
  );
}

// Inventory/Release prompt - shows when guest tries to save data
export function InventoryPrompt() {
  const { showInventoryPrompt, dismissInventoryPrompt } = useGuest();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!showInventoryPrompt || user) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-scale-up">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <CloudOff className="w-8 h-8 text-amber-500" />
          </div>
          
          <h2 className="text-xl font-bold mb-2">Save Across Devices</h2>
          <p className="text-muted-foreground mb-6">
            Sign in to save your inventory and releases across all your devices. Your data will sync automatically.
          </p>

          <div className="space-y-3">
            <Button
              className="w-full h-12 text-base font-semibold"
              onClick={() => {
                dismissInventoryPrompt(false);
                navigate('/auth');
              }}
            >
              Sign In to Save
            </Button>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={() => dismissInventoryPrompt(true)}
            >
              Continue as Guest
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
            <CloudOff className="w-3 h-3" />
            Guest data is stored locally and may be lost on device change
          </p>
        </div>
      </div>
    </div>
  );
}

// All prompts combined
export function GuestPrompts() {
  return (
    <>
      <SoftPromptBanner />
      <LimitOverlay />
      <InventoryPrompt />
    </>
  );
}
